'use client'

import { useEffect } from 'react'

export interface HashScrollHandlerProps {
  /** Offset from top for fixed headers (default: 80) */
  offset?: number
}

/**
 * Handles smooth scrolling to hash anchors with offset for fixed headers.
 * Renders nothing - just adds scroll behavior.
 *
 * @example
 * ```tsx
 * <HashScrollHandler offset={80} />
 * ```
 */
export function HashScrollHandler({ offset = 80 }: HashScrollHandlerProps) {
  useEffect(() => {
    const handleHashScroll = () => {
      const hash = window.location.hash
      if (hash) {
        const element = document.getElementById(hash.slice(1))
        if (element) {
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          })
        }
      }
    }

    // Handle initial hash on page load
    if (window.location.hash) {
      setTimeout(handleHashScroll, 100)
    }

    window.addEventListener('hashchange', handleHashScroll)

    return () => {
      window.removeEventListener('hashchange', handleHashScroll)
    }
  }, [offset])

  return null
}
